import React from "react";
import { Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import packageSample from "../../Assets/package-sample.webp";
import privateLabelPackage from "../../Assets/private-label-package.webp";
import manufactureImage from "../../Assets/blogs/manufacture.webp";
import blogHero from "../../Assets/blogs/blog-hero.webp";
import "./PrivateLabelSections.scss";

const privateLabelPoints = [
  "Private label laundry detergent sheets",
  "Formula and fragrance options",
  "Retail package options",
  "Bulk and refill-style programs",
  "Sample support before production",
  "Export support for international buyers",
  "Suitable for brands, retailers, wholesalers, refill shops, and distributors",
];

const processSteps = [
  "Submit your product inquiry",
  "Discuss formula, fragrance, package, and target market",
  "Review available samples",
  "Confirm package type and receive dielines",
  "Submit artwork for package review",
  "Approve product and package samples",
  "Start production and export planning",
];

export function SolutionHero({ onContact }) {
  return (
    <section className="solution-content-section solution-hero" aria-labelledby="solution-hero-title">
      <Container>
        <div className="solution-split solution-hero-layout">
          <div className="solution-section-copy">
            <p className="solution-eyebrow">Private Label Manufacturer</p>
            <h1 id="solution-hero-title">Private Label Laundry Sheets Manufacturer</h1>
            <p className="solution-subheadline">
              Klean Leaf helps brands develop private label laundry detergent sheets and related
              home-care products with formula, packaging, sample, and export support.
            </p>
            <div className="solution-actions">
              <button type="button" className="solution-btn solution-btn-light" onClick={onContact}>
                Request a Private Label Quote
              </button>
              <a
                href="/catalogs/klean-leaf-catalog.pdf"
                className="solution-btn solution-btn-outline-light"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Product Catalog PDF
              </a>
              <Link to="/products" className="solution-btn solution-btn-outline-light">
                Explore Private Label Products
              </Link>
            </div>
          </div>
          <div className="solution-hero-media">
            <img
              src={packageSample}
              alt="Private label laundry detergent sheet packaging manufactured by Klean Leaf"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </Container>
    </section>
  );
}

export function PrivateLabelSupport({ onContact }) {
  return (
    <section
      id="private-label-support"
      className="solution-content-section solution-private-label-support"
      aria-labelledby="solution-support-title"
    >
      <Container>
        <div className="solution-split solution-support-layout">
          <div className="solution-support-media">
            <img
              src={privateLabelPackage}
              alt="White private label laundry detergent sheets manufactured by Klean Leaf"
              className="solution-feature-image"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="solution-section-copy">
            <p className="solution-eyebrow">Private Label Support</p>
            <h2 id="solution-support-title">Built for Laundry Brands, Retailers, and Distributors</h2>
            <p>
              Klean Leaf works with B2B customers who want to launch, expand, or improve a <Link className="product-context-link" to="/products/laundry-detergent-sheets">laundry
              detergent sheet</Link> product line. We can support formula discussion, fragrance selection,
              packaging structure, dieline preparation, sample review, production planning, and export
              coordination.
            </p>
            <p>
              Whether you are building a new brand, adding a laundry sheet SKU to an existing product
              range, or sourcing a more competitive manufacturing solution, our team can help you move
              from product idea to finished retail package.
            </p>
            <p>
              Start your formula discussion by comparing <Link className="product-context-link" to="/products?category=sheets&q=enzyme">enzyme laundry sheet options</Link> or <Link className="product-context-link" to="/products/pva-free-laundry-detergent-sheets">PVA-free laundry detergent sheets</Link>. Review the product specifications and packaging options before requesting samples.
            </p>
            <ul className="solution-check-list">
              {privateLabelPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <div className="solution-actions">
              <button type="button" className="solution-btn solution-btn-primary" onClick={onContact}>
                Contact Us About Laundry Sheets
              </button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export function PrivateLabelProcess() {
  return (
    <section
      id="private-label-process"
      className="solution-content-section solution-process-section"
      aria-labelledby="solution-process-title"
    >
      <Container>
        <div className="solution-section-heading">
          <p className="solution-eyebrow">How It Works</p>
          <h2 id="solution-process-title">From Product Idea to Private Label Order</h2>
          <p>
            Klean Leaf helps customers move through the private label process step by step. After we
            understand your target product, market, fragrance direction, packaging preference, and
            order plan, we can prepare samples, confirm packaging details, provide dielines, review
            artwork, and move into production after approval.
          </p>
        </div>
        <ol className="solution-process-list">
          {processSteps.map((step, index) => (
            <li key={step}>
              <span className="solution-process-number" aria-hidden="true">{index + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}

export function ManufacturingSupport({ onContact }) {
  return (
    <section
      id="manufacturing-support"
      className="solution-content-section solution-manufacturing-support"
      aria-labelledby="solution-manufacturing-title"
    >
      <Container>
        <div className="solution-split solution-manufacturing-layout">
          <div className="solution-section-copy">
            <p className="solution-eyebrow">Private Label Manufacturing Support</p>
            <h2 id="solution-manufacturing-title">Established Manufacturing With North American Support</h2>
            <p>
              Klean Leaf helps brands, retailers, wholesalers, and distributors develop private label
              laundry and home-care products, with laundry detergent sheets as our main focus.
            </p>
            <p>
              From formula direction and fragrance options to packaging, samples, production
              planning, and export coordination, our team supports a smoother path from product idea
              to retail-ready goods.
            </p>
            <p>
              Building a wider laundry collection? Explore <Link className="product-context-link" to="/products/laundry-detergent-tablets">laundry detergent tablets</Link>, <Link className="product-context-link" to="/products/laundry-pods">laundry detergent pods</Link>, and <Link className="product-context-link" to="/products/fragrance-booster-beads">fragrance booster beads</Link> for your product shortlist.
            </p>
            <p>
              For home care, compare <Link className="product-context-link" to="/products/dishwasher-tablets">dishwasher detergent tablets</Link>, <Link className="product-context-link" to="/products/multi-purpose-cleaning-tablets">multi-purpose cleaning tablets</Link>, and <Link className="product-context-link" to="/products/glass-cleaning-tablets">glass cleaning tablets</Link>, or explore <Link className="product-context-link" to="/products?category=kit">cleaning and hand soap kits</Link>. Our team can discuss the product, package, and order requirements for your selection.
            </p>
            <div className="solution-actions">
              <Link
                to="/blogs/how-to-start-private-label-laundry-detergent-sheets-brand"
                className="solution-btn solution-btn-light"
              >
                How to Start Your Laundry Sheets Brand
              </Link>
              <button type="button" className="solution-btn solution-btn-outline-light" onClick={onContact}>
                Start Your Private Label Project
              </button>
            </div>
          </div>
          <div className="solution-manufacturing-media">
            <img
              src={manufactureImage}
              alt="Klean Leaf manufacturing support facility"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </Container>
    </section>
  );
}

export function LaunchResources({ onContact }) {
  return (
    <section
      id="launch-resources"
      className="solution-content-section solution-launch-resources"
      aria-labelledby="solution-resources-title"
    >
      <Container>
        <div className="solution-split solution-resources-layout">
          <div className="solution-resources-media">
            <img
              src={blogHero}
              alt="Klean Leaf laundry detergent sheets blog guide"
              className="solution-feature-image"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="solution-section-copy">
            <p className="solution-eyebrow">Learn More</p>
            <h2 id="solution-resources-title">Learn Before You Launch</h2>
            <p>
              Explore Klean Leaf blog guides for product development, packaging, samples, and launch
              planning. You can also learn more about our company story and manufacturing support.
            </p>
            <div className="solution-actions">
              <Link to="/blog" className="solution-btn solution-btn-primary">
                Read Klean Leaf Blog
              </Link>
              <button type="button" className="solution-btn solution-btn-secondary" onClick={onContact}>
                Request Samples
              </button>
              <Link to="/about" className="solution-btn solution-btn-secondary">
                About Klean Leaf
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
